import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import Image from 'src/components/image';
import { ImageContainer } from 'src/components/image/Image';
import {
  IBusiness,
  ICategory,
  ICodeModel,
  IEmployee,
  IEvent,
  IGroup,
  IHistory,
  IMainVisual,
  IMaterials,
  INews,
  INotice,
  IOperatorInfo,
  IPartner,
  IPopup,
  IRecruit,
  useStores,
} from 'src/models';
import { DEFAULT_IMAGE_ACCEPT_TYPE, DEFAULT_PREVIEW, MG_FORMAT } from 'src/static/constants';
import { getFileType, getFileUrl } from 'src/utils/fileUploadUtils';
import { fDate } from 'src/utils/formatTime';

import useRenderUseYnCell from './renderUseYnCell';
/**
 * ## useGetGridCol
 * - Grid Col을 가져오는 Hook
 */

export interface IPopupGridRow extends IPopup {
  id: number;
}

export interface IRecruitGridRow extends IRecruit {
  id: number;
}
export interface IMainVisualGridRow extends IMainVisual {
  id: number;
}
export interface IBusinessGridRow extends IBusiness {
  id: number;
}
export interface ICategoryGridRow extends ICategory {
  id: string;
}
export interface IHistoryGridRow extends IHistory {
  id: number;
}
export interface IPartnerGridRow extends IPartner {
  id: number;
}
export interface IEventGridRow extends IEvent {
  id: number;
}
export interface IMaterialsGridRow extends IMaterials {
  id: number;
}
export interface INewsGridRow extends INews {
  id: number;
}
export interface INoticeGridRow extends INotice {
  id: number;
}
export interface IEmployeeGridRow extends IEmployee {
  id: number;
}
export interface IGroupGridRow extends IGroup {
  id: number;
}

// 계정 삭제 여부 판단해서 값 반환
const getValueOrDefault = (row: IEmployee, value: any, defaultValue: string) => {
  return row.wthdYn ? defaultValue : value ?? defaultValue;
};
// html 태그 제거
const stripHtmlTags = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

function useGetGridCol() {
  const { commonCodeStore } = useStores();
  const { newsSelectOptions } = commonCodeStore;
  const renderUseYnCell = useRenderUseYnCell();
  // 팝업
  function getPopupGridCol(): GridColDef<IPopupGridRow>[] {
    return [
      {
        field: 'title',
        headerName: '제목',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
      },
      {
        field: 'useYn',
        headerName: '사용 여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return renderUseYnCell(params.row.useYn);
        },
      },
      {
        field: 'startDt',
        headerName: '적용일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
      {
        field: 'endDt',
        headerName: '종료일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
      {
        field: 'width',
        headerName: '가로 크기',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'height',
        headerName: '세로 크기',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'regUser',
        headerName: '등록자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: { oprtrNm: string }) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 채용
  function getRecruitGridCol(): GridColDef<IRecruitGridRow>[] {
    return [
      {
        field: 'expCd',
        headerName: '경력여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (params: ICodeModel) => params?.value ?? '-',
      },
      {
        field: 'applyFieldCd',
        headerName: '지원분야',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (params: ICodeModel) => params?.value ?? '-',
      },
      {
        field: 'nameKr',
        headerName: '이름',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'phone',
        headerName: '전화번호',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'birthDate',
        headerName: '생년월일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATE_FORMAT),
      },
      {
        field: 'viewYn',
        headerName: '확인여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return renderUseYnCell(params.row.viewYn);
        },
      },
      {
        field: 'recruitStatusCd',
        headerName: '합격여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (params: ICodeModel) => params?.value ?? '-',
      },
      {
        field: 'updUser',
        headerName: '처리자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: IOperatorInfo) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 컨텐츠 - 메인 비주얼 관리 그리드 컬럼 정의
  function getMainVisualGridCol(): GridColDef<IMainVisualGridRow>[] {
    return [
      {
        field: 'lang',
        headerName: '언어',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'basckgroundImage',
        headerName: '배경 이미지',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        renderCell: (params: GridRenderCellParams) => {
          // bgPath가 있는지 먼저 체크
          if (!params.row.bgPath?.length) {
            return <Typography>{'-'}</Typography>;
          }

          const fileType = getFileType(params.row.bgPath[0], DEFAULT_IMAGE_ACCEPT_TYPE);
          const { type } = fileType;
          const fileURL = getFileUrl(params.row.bgPath[0]);

          if (type === 'image') {
            return (
              <ImageContainer>
                <Image
                  src={fileURL}
                  sx={{
                    height: 100,
                    width: 100,
                    objectFit: 'cover',
                    display: 'block',
                    borderRadius: 2,
                  }}
                  onError={(e) => {
                    console.error('Image load error:', e);
                    (e.target as HTMLImageElement).src = DEFAULT_PREVIEW;
                  }}
                />
              </ImageContainer>
            );
          } else if (type === 'video') {
            return (
              <ImageContainer>
                <video controls style={{ borderRadius: 8, height: 100, width: 100 }}>
                  <source src={fileURL} type="video/mp4" />
                </video>
              </ImageContainer>
            );
          } else {
            return <Typography>{'-'}</Typography>;
          }
        },
      },
      {
        field: 'mainText',
        headerName: '메인 텍스트',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        valueGetter: (v: string) => (v ? stripHtmlTags(v) : '-'),
      },
      {
        field: 'subText1',
        headerName: '서브 텍스트1',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        valueGetter: (v: string) => (v ? stripHtmlTags(v) : '-'),
      },
      {
        field: 'subText2',
        headerName: '서브 텍스트2',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        valueGetter: (v: string) => (v ? stripHtmlTags(v) : '-'),
      },
      {
        field: 'ordr',
        headerName: '노출 순서',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'useYn',
        headerName: '사용 여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return renderUseYnCell(params.row.useYn);
        },
      },
      {
        field: 'regUser',
        headerName: '등록자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: { oprtrNm: string }) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 컨텐츠 - 사업 관리 그리드 컬럼 정의
  function getBusinessGridCol(): GridColDef<IBusinessGridRow>[] {
    return [
      // { field: 'id' },
      {
        field: 'lang',
        headerName: '언어',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'thumbPath',
        headerName: '썸네일 이미지',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        renderCell: (params: GridRenderCellParams) => {
          const fileURL = getFileUrl(params.row.thumbPath);
          return (
            <ImageContainer>
              {fileURL ? (
                <Image src={fileURL} sx={{ height: 100, width: 100 }} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  -
                </Typography>
              )}
            </ImageContainer>
          );
        },
      },

      {
        field: 'bnCatCd',
        headerName: '카테고리',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'title',
        headerName: '제목',
        headerAlign: 'center',
        align: 'left',
        flex: 3,
      },
      {
        field: 'useYn',
        headerName: '사용 여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return renderUseYnCell(params.row.useYn);
        },
      },
      {
        field: 'ordr',
        headerName: '노출 순서',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'regUser',
        headerName: '등록자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: { oprtrNm: string }) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 카테고리
  function getCategryGridCol(): GridColDef<ICategoryGridRow>[] {
    return [
      {
        field: 'categoryNm',
        headerName: '카테고리',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
      },
      {
        field: 'pCategory',
        headerName: '상위 카테고리',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'useYn',
        headerAlign: 'center',
        align: 'center',
        flex: 0.5,
        headerName: '사용여부',
        renderCell: (params: GridRenderCellParams) => {
          return renderUseYnCell(params.row.useYn);
        },
      },
      {
        field: 'order',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        headerName: '노출순서',
      },
    ];
  }

  // 회사 연혁
  function getHistoryGridCol(): GridColDef<IHistoryGridRow>[] {
    return [
      {
        field: 'historyDate',
        headerName: '날짜',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATE_FORMAT),
      },
      {
        field: 'titleKo',
        headerName: '제목(한글)',
        headerAlign: 'center',
        align: 'left',
        flex: 2,
      },
      {
        field: 'titleEn',
        headerName: '제목(영문)',
        headerAlign: 'center',
        align: 'left',
        flex: 2,
      },
      {
        field: 'regUser',
        headerName: '등록자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: { oprtrNm: string }) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 회사 파트너
  function getPartnerGridCol(): GridColDef<IPartnerGridRow>[] {
    return [
      {
        field: 'logoImgPath',
        headerName: '로고 이미지',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        renderCell: (params: GridRenderCellParams) => {
          return params.row.logoImgPath ? (
            <ImageContainer>
              <Image
                src={process.env.REACT_APP_API_URL + params.row.logoImgPath}
                sx={{ height: '60%', width: 'auto', objectFit: 'contain' }}
              />
            </ImageContainer>
          ) : (
            <Typography>{'-'}</Typography>
          );
        },
      },
      {
        field: 'partnerName',
        headerName: '이름',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
      },
      {
        field: 'categoryType',
        headerName: '유형',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'useYn',
        headerName: '사용여부',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          return renderUseYnCell(params.row.useYn);
        },
      },
      {
        field: 'regUser',
        headerName: '등록자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: { oprtrNm: string }) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 이벤트(스케줄)
  function getEventGridCol(): GridColDef<IEventGridRow>[] {
    return [
      {
        field: 'eventTypeCd',
        headerName: '유형',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (params: ICodeModel) => params.value ?? '-',
      },
      {
        field: 'title',
        headerName: '제목',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'location',
        headerName: '장소',
        headerAlign: 'center',
        align: 'center',
        flex: 3,
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 자료실
  function getMaterialsGridCol(): GridColDef<IMaterialsGridRow>[] {
    return [
      {
        field: 'lang',
        headerName: '언어',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'title',
        headerName: '제목',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'file',
        headerName: '파일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'type',
        headerName: '유형',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: '',
        headerName: '다운로드',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 뉴스
  function getNewsGridCol(): GridColDef<INewsGridRow>[] {
    return [
      {
        field: 'no',
        headerName: 'ID',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'prNewsCtgyCd',
        headerName: '카테고리',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (params: number) => {
          const code = toJS(newsSelectOptions)?.find((v) => v.value === params);
          return code?.label ?? '-';
        },
      },
      {
        field: 'title',
        headerName: '제목',
        headerAlign: 'center',
        align: 'left',
        flex: 3,
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 공지사항
  function getNoticeGridCol(): GridColDef<INoticeGridRow>[] {
    return [
      {
        field: 'no',
        headerName: 'ID',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'title',
        headerName: '제목',
        headerAlign: 'center',
        align: 'left',
        flex: 3,
      },
      {
        field: 'regUser',
        headerName: '등록자',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (v: { oprtrNm: string }) => v?.oprtrNm ?? '-',
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 직원(운영자)
  function getEmployeeGridCol(): GridColDef<IEmployeeGridRow>[] {
    return [
      {
        field: 'loginId',
        headerName: '로그인 ID',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
      },
      {
        field: 'oprtrNm',
        headerName: '이름',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
      },
      {
        field: 'oprtrSttsCd',
        headerAlign: 'center',
        align: 'center',
        flex: 0.5,
        headerName: '계정상태',
        valueGetter: (params: ICodeModel, row: IEmployee) =>
          getValueOrDefault(row, params ? params?.value : '-', '탈퇴'),
      },
      {
        field: 'lastLoginDt',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        headerName: '최근접속일시',
        valueGetter: (params, row: IEmployee) =>
          getValueOrDefault(row, fDate(params, MG_FORMAT.DATETIME_FORMAT), '-'),
      },
      {
        field: 'regDt',
        headerName: '등록일',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
      {
        field: 'updDt',
        headerName: '수정일',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 그룹
  function getGroupGridCol(): GridColDef<IGroupGridRow>[] {
    return [
      {
        field: 'oprtrGroupNm',
        headerAlign: 'center',
        align: 'left',
        flex: 2,
        headerName: '그룹명',
      },
      { field: 'dscrpt', headerAlign: 'center', align: 'left', flex: 4, headerName: '설명' },
      {
        field: 'regDt',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        headerName: '등록일시',
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
      {
        field: 'updDt',
        headerAlign: 'center',
        align: 'center',
        flex: 1.5,
        headerName: '수정일시',
        valueGetter: (param) => fDate(param, MG_FORMAT.DATETIME_FORMAT),
      },
    ];
  }
  // 메뉴 관리의 그룹리스트 그리드
  function getMenuGroupGridCol(): GridColDef<IGroupGridRow>[] {
    return [
      {
        field: 'oprtrGroupNm',
        headerAlign: 'center',
        align: 'left',
        flex: 2,
        headerName: '그룹명',
      },
      { field: 'dscrpt', headerAlign: 'center', align: 'left', flex: 4, headerName: '설명' },
    ];
  }

  return {
    getPopupGridCol,
    getRecruitGridCol,
    getMainVisualGridCol,
    getBusinessGridCol,
    getHistoryGridCol,
    getPartnerGridCol,
    getEventGridCol,
    getMaterialsGridCol,
    getNewsGridCol,
    getNoticeGridCol,
    getEmployeeGridCol,
    getGroupGridCol,
    getCategryGridCol,
    getMenuGroupGridCol,
  };
}

export default useGetGridCol;
